/**
 * Adds a part that was found at a location the stocktake did not list (#3031).
 * The part comes from the catalogue and the location from the stocktake's own
 * warehouse (or its single bin), so a found surplus can be counted and, once
 * approved, enters the stock ledger like any other correction.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useInventoryParts, useWarehouses, type PartListItem } from '@/hooks/useInventory';
import { useDebounce } from '@/hooks/useDebounce';
import { useNotify } from '@/stores/notificationStore';
import { apiErrorCode, useAddStocktakeLine, type StocktakeLine, type StocktakeSession } from './stocktakeSessions';

/** Catalogue matches offered at a time; the search narrows them. */
const PART_OPTIONS = 20;

const KNOWN_ERRORS = ['STOCKTAKE_LINE_EXISTS', 'STOCKTAKE_INVALID_SCOPE', 'STOCKTAKE_LINE_NOT_FOUND', 'STOCKTAKE_SCOPE_TOO_LARGE'];

export function AddFoundPartDialog({
  open,
  onClose,
  session,
  onAdded,
}: {
  open: boolean;
  onClose: () => void;
  session: Pick<StocktakeSession, 'id' | 'warehouseId' | 'binLocationId'>;
  onAdded: (line: StocktakeLine, part: PartListItem) => void;
}) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const add = useAddStocktakeLine(session.id);
  const warehousesQuery = useWarehouses();
  const [part, setPart] = useState<PartListItem | null>(null);
  const [input, setInput] = useState('');
  const [binLocationId, setBinLocationId] = useState(session.binLocationId ?? '');
  const search = useDebounce(input.trim(), 300);

  useEffect(() => {
    if (open) {
      setPart(null);
      setInput('');
      setBinLocationId(session.binLocationId ?? '');
    }
  }, [open, session.binLocationId]);

  const partsQuery = useInventoryParts(
    { isActive: true, limit: PART_OPTIONS, ...(search === '' ? {} : { q: search }) },
    { enabled: open },
  );
  const options = useMemo(
    () => (partsQuery.data?.pages ?? []).flatMap((page) => page.data).slice(0, PART_OPTIONS),
    [partsQuery.data],
  );
  const bins = useMemo(
    () => warehousesQuery.data?.data.find((w) => w.id === session.warehouseId)?.binLocations ?? [],
    [warehousesQuery.data, session.warehouseId],
  );
  // A stocktake of one bin can only receive parts in that bin.
  const binFixed = session.binLocationId !== null;

  const submit = () => {
    if (!part) return;
    add.mutate(
      { partId: part.id, binLocationId: binLocationId === '' ? null : binLocationId },
      {
        onSuccess: (line) => {
          notify.success(t('stocktakeSessions.addFoundDone'), t('stocktakeSessions.addFoundDoneDesc', { name: part.name }));
          onAdded(line, part);
          onClose();
        },
        onError: (error) => {
          const code = apiErrorCode(error);
          notify.error(
            tc('error'),
            code && KNOWN_ERRORS.includes(code) ? t(`stocktakeSessions.errors.${code}`) : t('stocktakeSessions.errors.addFailed'),
          );
        },
      },
    );
  };

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="sm" fullWidth mobileTitle={t('stocktakeSessions.addFoundTitle')}>
      <DialogTitle>{t('stocktakeSessions.addFoundTitle')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
          <Alert severity="info">{t('stocktakeSessions.addFoundInfo')}</Alert>
          <Autocomplete
            options={options}
            value={part}
            onChange={(_, value) => setPart(value)}
            inputValue={input}
            onInputChange={(_, value, reason) => {
              if (reason !== 'reset') setInput(value);
            }}
            // The server already filtered by the search text.
            filterOptions={(all) => all}
            getOptionLabel={(option) => `${option.name} (${option.sku})`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loading={partsQuery.isLoading}
            noOptionsText={t('stocktakeSessions.addFoundNoParts')}
            renderInput={(params) => (
              <TextField {...params} autoFocus label={t('stocktakeSessions.addFoundPart')} helperText={t('stocktakeSessions.addFoundPartHint')} />
            )}
          />
          <FormControl fullWidth disabled={binFixed}>
            <InputLabel id="stocktake-found-bin-label">{t('stocktakeSessions.addFoundBin')}</InputLabel>
            <Select
              labelId="stocktake-found-bin-label"
              label={t('stocktakeSessions.addFoundBin')}
              value={binLocationId}
              onChange={(e) => setBinLocationId(e.target.value)}
              displayEmpty
              notched
            >
              {!binFixed && <MenuItem value="">{t('stocktakeSessions.noBin')}</MenuItem>}
              {bins
                .filter((bin) => !binFixed || bin.id === session.binLocationId)
                .map((bin) => (
                  <MenuItem key={bin.id} value={bin.id}>
                    {bin.code}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {binFixed && <Alert severity="info">{t('stocktakeSessions.addFoundBinFixed')}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          {tc('cancel')}
        </Button>
        <Button variant="contained" onClick={submit} disabled={!part || add.isPending}>
          {t('stocktakeSessions.addFoundAction')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
